// src/pages/Question/QuestionPage.jsx
import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Seo from '../../components/Seo/Seo';
import UserAvatar from '../../components/UI/UserAvatar';
import { getBackgroundPatternStyle } from '../../components/utils/backgroundPatterns';
import {
  buildPublicProfilePath,
  buildPublicQuestionPath,
} from '../../components/utils/profilePaths';

export default function QuestionPage() {
  const {
    userId: paramUserId,
    username: legacyUsername,
    questionId,
  } = useParams();
  const navigate = useNavigate();
  const shouldCanonicalizeRoute = Boolean(paramUserId || legacyUsername);

  const [qna, setQna] = useState(null);
  const [profile, setProfile] = useState(null);

  // 1) Load the Q&A
  useEffect(() => {
    fetch(`/api/questions/${questionId}`)
      .then(r => r.json())
      .then(setQna)
      .catch(console.error);
  }, [questionId]);

  // 2) Load the expert’s profile (to get avatarUrl)
  useEffect(() => {
    const profileLookupUrl = paramUserId
      ? `/api/users/public/id/${paramUserId}`
      : `/api/users/public/${legacyUsername}`;

    fetch(profileLookupUrl)
      .then(r => r.json())
      .then(setProfile)
      .catch(console.error);
  }, [legacyUsername, paramUserId]);

  useEffect(() => {
    if (!shouldCanonicalizeRoute || !qna || !profile) {
      return;
    }

    const canonicalPath = buildPublicQuestionPath(profile, qna);
    if (canonicalPath && window.location.pathname !== canonicalPath) {
      navigate(canonicalPath, { replace: true });
    }
  }, [navigate, profile, qna, shouldCanonicalizeRoute]);

  if (!qna || !profile) return <div>Loading…</div>;

  // Destructure fields including source
  const { question, detail, answer, source } = qna;

  // SEO values
  const title        = `${qna.question} — answered by ${profile.name} | Ques`;
  const description  = qna.answer.slice(0, 150) + '…';
  const canonicalProfilePath = buildPublicProfilePath(profile);
  const canonicalQuestionPath = buildPublicQuestionPath(profile, qna);
  const canonicalUrl = `https://askques.com${canonicalQuestionPath}`;
  const ogImageUrl   = profile.avatarUrl
    ? `https://askques.com${profile.avatarUrl}`
    : 'https://askques.com/images/og-image.png';

  // Prepare avatar URL with cache‐busting
  const resolvedAvatarUrl = profile.avatarUrl
    ? profile.avatarUrl === '/images/user-image.png'
      ? profile.avatarUrl
      : `${profile.avatarUrl}?v=${new Date().getTime()}`
    : '/images/user-image.png';

  // Structured‐data
  const qapageStructuredData = {
    "@context":"https://schema.org",
    "@type":"QAPage",
    "mainEntity": {
      "@type":"Question",
      "name":qna.question,
      "text":qna.question,
      "answerCount":1,
      "acceptedAnswer":{"@type":"Answer","text":qna.answer}
    }
  };

  const themeStyles = {
    '--theme-primary': profile.themePrimary || '#1A140F',
    '--theme-accent': profile.themeAccent || '#FAF6F2',
    '--theme-primary-text': profile.themePrimaryText || '#1A140F',
    '--theme-accent-text': profile.themeAccentText || '#FAF6F2',
    '--theme-background': profile.themeBackground || '#FFFFFF',
    '--theme-background-text': profile.themeBackgroundText || '#1A140F',
  };

  const questionPageStyle = getBackgroundPatternStyle({
    pattern: profile?.backgroundPattern,
    opacity: profile?.backgroundPatternOpacity,
    backgroundColor: profile?.themeBackground || '#FFFFFF',
    foregroundColor: profile?.themeBackgroundText || '#1A140F',
  });

  return (
    <>
      <Seo
        title={title}
        description={description}
        canonicalUrl={canonicalUrl}
        ogImageUrl={ogImageUrl}
      />

      <article
        className="question-page"
        style={{ ...themeStyles, ...questionPageStyle }}
      >
        <div className="question-page-container">
          <div className="question-page-content card">
            {/* Q badge */}
            <div className="badge-question small">
              <div className="badge-question-number small">Q</div>
            </div>

            {/* Question */}
            <h4>{qna.question}</h4>
            {qna.detail && <p className="margin-top-m">{qna.detail}</p>}

            <div className="divider dotted" />

            {/* A badge */}
            <div className="badge-question small">
              <div className="badge-question-number small">A</div>
            </div>

            {/* Answer */}
            <p>{qna.answer}</p>

            {/* profile image below answer */}
            <div className="profile-avatar-wrapper">
              <UserAvatar
                avatarUrl={resolvedAvatarUrl}
                userName={profile.name}
                alt={`Avatar of ${profile.name}`}
              />
            </div>
            {source && 
              <div className="source-label">
                <svg
                  className="source-label-icon"
                  width="21"
                  height="20"
                  viewBox="0 0 21 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <g opacity="0.5">
                    <path
                      d="M10.2497 5.82422C10.0136 5.82422 9.81565 5.90408 9.65593 6.0638C9.4962 6.22352 9.41634 6.42144 9.41634 6.65755C9.41634 6.89366 9.4962 7.09158 9.65593 7.2513C9.81565 7.41102 10.0136 7.49088 10.2497 7.49088C10.4858 7.49088 10.6837 7.41102 10.8434 7.2513C11.0031 7.09158 11.083 6.89366 11.083 6.65755C11.083 6.42144 11.0031 6.22352 10.8434 6.0638C10.6837 5.90408 10.4858 5.82422 10.2497 5.82422ZM11.083 9.15755L9.41634 9.15755L9.41634 14.1576L11.083 14.1576L11.083 9.15755ZM10.2497 1.65755C11.4025 1.65755 12.4858 1.8763 13.4997 2.3138C14.5136 2.7513 15.3955 3.34505 16.1455 4.09505C16.8955 4.84505 17.4893 5.727 17.9268 6.74088C18.3643 7.75477 18.583 8.83811 18.583 9.99089C18.583 11.1437 18.3643 12.227 17.9268 13.2409C17.4893 14.2548 16.8955 15.1367 16.1455 15.8867C15.3955 16.6367 14.5136 17.2305 13.4997 17.668C12.4858 18.1055 11.4025 18.3242 10.2497 18.3242C9.0969 18.3242 8.01356 18.1055 6.99968 17.668C5.98579 17.2305 5.10384 16.6367 4.35384 15.8867C3.60384 15.1367 3.01009 14.2548 2.57259 13.2409C2.13509 12.227 1.91634 11.1437 1.91634 9.99088C1.91634 8.83811 2.1351 7.75477 2.5726 6.74088C3.0101 5.72699 3.60384 4.84505 4.35384 4.09505C5.10384 3.34505 5.98579 2.7513 6.99968 2.3138C8.01357 1.8763 9.0969 1.65755 10.2497 1.65755ZM10.2497 3.32422C8.38857 3.32422 6.81218 3.97005 5.52051 5.26172C4.22884 6.55338 3.58301 8.12977 3.58301 9.99088C3.58301 11.852 4.22884 13.4284 5.52051 14.7201C6.81218 16.0117 8.38857 16.6576 10.2497 16.6576C12.1108 16.6576 13.6872 16.0117 14.9788 14.7201C16.2705 13.4284 16.9163 11.852 16.9163 9.99088C16.9163 8.12977 16.2705 6.55338 14.9788 5.26172C13.6872 3.97005 12.1108 3.32422 10.2497 3.32422Z"
                      fill="currentColor"
                    />
                  </g>
                </svg>
                <p>{source}</p>
              </div>
            }
            {/* Link back to profile */}
            <Link to={canonicalProfilePath}>
              <button className="text-link margin-top-m">
                View more answers from {profile.name}
              </button>
            </Link>
          </div>
          <div className="question-page-back">
            <Link to={canonicalProfilePath}>
              <button>
                <svg
                  className="qa-feature-layout__question-indicator-icon qa-feature-layout__question-indicator-icon--active"
                  width="8"
                  height="6"
                  viewBox="0 0 8 6"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    d="M5.33333 5.33333L4.625 4.625L6.08333 3.16667H0V2.16667H6.08333L4.625 0.708334L5.33333 0L8 2.66667L5.33333 5.33333Z"
                    fill="currentColor"
                  />
                </svg>
              </button>
            </Link>
          </div>
        </div>
      </article>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(qapageStructuredData) }}
      />
    </>
  );
}
